import { trpc } from "@/lib/trpc";
import { initPostHog } from "./lib/posthog";
import { getTrpcUrl } from "@/lib/env";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";
import "./styles/print.css";
// [BUG-DESKTOP-MAP-5 FIX] Import Leaflet CSS globally to ensure it is always bundled.
// Dynamic `import('leaflet/dist/leaflet.css')` inside async initMap() can be tree-shaken
// or code-split away in production builds, causing the map container to be present but
// tiles/panes invisible (missing absolute positioning/z-index/overflow rules).
// Importing here guarantees the CSS is always in the main bundle regardless of route.
import "leaflet/dist/leaflet.css";
import { setupGlobalErrorHandler } from "@/lib/errorLogger";
import { initSentry } from "@/lib/sentry";
import { initWebVitals } from "@/lib/webVitals";

// Initialize Sentry first
initSentry();
initPostHog();
// Track Core Web Vitals (LCP, CLS, INP, FCP, TTFB) → Sentry Performance
initWebVitals();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // ປ້ອງກັນ retry failed queries
      refetchOnWindowFocus: false, // ປ້ອງກັນ refetch ເມື່ອ focus window
      staleTime: 30 * 1000, // 30 seconds - ຫຼຸດ requests ຊ້ຳ
      gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  // Check if error message indicates unauthorized
  const isUnauthorized =
    error.message?.includes("Please login") || error.message?.includes("10001");

  if (!isUnauthorized) return;

  // Don't redirect if already on login page or other public routes
  // This prevents infinite redirect loops
  const currentPath = window.location.pathname;
  const publicRoutes = ["/login", "/forgot-password", "/change-password-required"];
  if (publicRoutes.some(route => currentPath.startsWith(route))) {
    return;
  }

  // Redirect to login page
  window.location.href = "/login";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    console.error(
      "[API Query Error] Query:",
      event.query.queryKey,
      "Error:",
      error
    );
    redirectToLoginIfUnauthorized(error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: getTrpcUrl(),
      transformer: superjson as any,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Setup global error handler
setupGlobalErrorHandler();

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

// Register Service Worker for PWA
// Use a flag to prevent multiple reloads
let isReloading = false;

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then(registration => {
        console.log("[PWA] Service Worker registered:", registration);

        // ກວດສອບວ່າມີ Service Worker ໃໝ່ຫຼືບໍ່
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (
                newWorker.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                // ມີ SW ໃໝ່ — activate ທັນທີ ເພື່ອປ້ອງກັນ stale JS chunk errors
                // (ERR-MP0ZNVUI-WTCM: Failed to fetch dynamically imported module)
                // ການ auto-activate ທັນທີ ປອດໄພ ຍ້ອນ SW ໃໝ່ໃຊ້ Network First ສຳລັບ assets
                console.log("[PWA] New version available — activating immediately to prevent stale chunks");
                if (!isReloading) {
                  isReloading = true;
                  newWorker.postMessage({ type: "SKIP_WAITING" });
                }
              }
            });
          }
        });

        // Check for updates periodically (every hour)
        setInterval(
          () => {
            registration.update();
          },
          60 * 60 * 1000
        );
      })
      .catch(error => {
        console.error("[PWA] Service Worker registration failed:", error);
      });
  });

  // ຟັງການ control change ເພື່ອ reload ໜ້າເມື່ອມີ SW ໃໝ່
  // Use a flag to prevent multiple reloads
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!isReloading) {
      isReloading = true;
      console.log("[PWA] Controller changed, reloading...");
      // Small delay to ensure the new SW is ready
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });
}
